import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import CarePriceUI from "./CarePriceUI";
import { getLowestPrice, getSelections } from "../../../../Features/Postes/Thunk/patientFlowThunk";
import API from "../../../../Api/Api";
import ENDPOINTS from "../../../../Api/Endpoints";
import {
  clearMatchResults,
  clearPatientFlowError,
} from "../../../../Features/Postes/Slice/patientFlowSlice";

const safeArray = (value) => (Array.isArray(value) ? value : []);

const pickId = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return "";
};

const getSelectionList = (selections) => {
  if (Array.isArray(selections)) return selections;
  if (Array.isArray(selections?.items)) return selections.items;
  if (Array.isArray(selections?.data?.selections)) return selections.data.selections;
  if (Array.isArray(selections?.data)) return selections.data;
  if (Array.isArray(selections?.selections)) return selections.selections;
  return [];
};

const getSelectionSubCategoryId = (selectionItem) => {
  if (!selectionItem) return "";
  // GET /app/selections returns expandSelections() shape: subCategories[{ subCategoryId, ... }]
  const fromExpanded = safeArray(selectionItem?.subCategories)?.[0];
  return pickId(
    fromExpanded?.subCategoryId,
    fromExpanded?._id,
    fromExpanded?.id,
    selectionItem?.subCategory?._id,
    selectionItem?.subCategory?.id,
    selectionItem?.selectedSubCategory?._id,
    selectionItem?.selectedSubCategory?.id,
    selectionItem?.subCategoryId,
    safeArray(selectionItem?.subCategoryIds)?.[0]?._id,
    safeArray(selectionItem?.subCategoryIds)?.[0]?.id,
    safeArray(selectionItem?.subCategoryIds)?.[0]
  );
};

/** Prefetch is usable if it matches the selected subcategory (bounds may be null — UI applies ₹0–₹30L fallback) or has finite bounds. */
const isPrefetchedPriceReady = (prefetched, subCategoryId) => {
  if (!prefetched || !subCategoryId) return false;
  const src = prefetched?.data || prefetched?.item || prefetched || {};
  const prefSub = pickId(src?.subCategoryId, prefetched?.subCategoryId);
  if (prefSub && String(prefSub) === String(subCategoryId)) return true;
  const lowest = Number(src?.lowestPrice ?? src?.minPrice ?? src?.minimumPrice);
  const highest = Number(src?.highestPrice ?? src?.maxPrice ?? src?.maximumPrice);
  return Number.isFinite(lowest) && Number.isFinite(highest);
};

const PREFETCH_CACHE_KEY = "healbid:careprice:prefetchedPrice";
const PREFETCH_CACHE_MAX_AGE_MS = 5 * 60 * 1000; // 5 min

/** Restore prefetched price from sessionStorage after e.g. reload on first location grant. */
const getRestoredPrefetchedPrice = (subCategoryId) => {
  if (!subCategoryId) return null;
  try {
    const raw = sessionStorage.getItem(PREFETCH_CACHE_KEY);
    if (!raw) return null;
    const { subCategoryId: cachedSub, prefetchedPriceInfo, ts } = JSON.parse(raw);
    if (String(cachedSub) !== String(subCategoryId)) return null;
    if (Date.now() - (ts || 0) > PREFETCH_CACHE_MAX_AGE_MS) return null;
    return prefetchedPriceInfo || null;
  } catch (_) {
    return null;
  }
};

const setRestoredPrefetchedPrice = (subCategoryId, prefetchedPriceInfo) => {
  if (!subCategoryId || !prefetchedPriceInfo) return;
  try {
    sessionStorage.setItem(
      PREFETCH_CACHE_KEY,
      JSON.stringify({
        subCategoryId: String(subCategoryId),
        prefetchedPriceInfo,
        ts: Date.now(),
      })
    );
  } catch (_) {}
};

export default function CarePriceGate() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const { selections, priceInfo, error } = useSelector((state) => state.patientFlow);

  const LAST_MATCH_REQUEST_ID_KEY = "healbid:careprice:lastMatchRequestId";

  const parseBooleanish = (value) => {
    if (value === true) return true;
    if (value === false) return false;
    if (value === 1 || value === "1") return true;
    if (value === 0 || value === "0" || value == null) return false;
    if (typeof value === "string") {
      const s = value.trim().toLowerCase();
      if (s === "true" || s === "yes" || s === "completed" || s === "complete") return true;
      if (s === "false" || s === "no" || s === "pending" || s === "incomplete") return false;
    }
    // Last resort: JS truthiness, but only after the above known-safe cases.
    return Boolean(value);
  };

  const [backendLatestMatchRequestId, setBackendLatestMatchRequestId] =
    useState(() => {
      try {
        const v = window.localStorage.getItem(LAST_MATCH_REQUEST_ID_KEY);
        return v && String(v).trim() ? String(v) : null;
      } catch (_) {
        return null;
      }
    });
  const [backendLatestTreatmentCompleted, setBackendLatestTreatmentCompleted] =
    useState(false);
  const [backendLatestLoading, setBackendLatestLoading] = useState(true);
  const routeMatchRequestId = String(location?.state?.matchRequestId || "").trim();
  /** Block Care Price when this match request is still in admin document review (or request-level rejected). */
  const [carePriceWorkflowBlock, setCarePriceWorkflowBlock] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setBackendLatestLoading(true);
      try {
        // If a specific request id was provided from review/details, gate against that request first.
        if (routeMatchRequestId) {
          const detailRes = await API.get(ENDPOINTS.GET_MATCH_REQUEST_DETAIL(routeMatchRequestId)).catch(() => null);
          const specific =
            detailRes?.data?.request ??
            detailRes?.request ??
            detailRes?.data?.matchRequest ??
            detailRes?.matchRequest ??
            null;
          if (specific) {
            const specificId = specific?.matchRequestId || specific?._id || routeMatchRequestId;
            const treatmentCompleted = parseBooleanish(specific?.treatmentCompleted);
            if (!cancelled) {
              setBackendLatestMatchRequestId(String(specificId));
              setBackendLatestTreatmentCompleted(treatmentCompleted);
              if (specificId && !treatmentCompleted) {
                try {
                  window.localStorage.setItem(LAST_MATCH_REQUEST_ID_KEY, String(specificId));
                } catch (_) {}
              }
              setBackendLatestLoading(false);
              return;
            }
          }
        }

        const res = await API.get(ENDPOINTS.GET_LATEST_MATCH_REQUEST);
        const mr =
          res?.data?.matchRequest ??
          res?.data?.data?.matchRequest ??
          res?.matchRequest ??
          null;
        const mrId = mr?.matchRequestId || mr?._id || null;
        const treatmentCompleted = parseBooleanish(mr?.treatmentCompleted);
        if (!cancelled) {
          // Keep local restore id if backend doesn't return an active match id.
          if (mrId) setBackendLatestMatchRequestId(String(mrId));
          else setBackendLatestMatchRequestId(null);
          setBackendLatestTreatmentCompleted(treatmentCompleted);

          if (mrId && !treatmentCompleted) {
            try {
              window.localStorage.setItem(
                LAST_MATCH_REQUEST_ID_KEY,
                String(mrId)
              );
            } catch (_) {}
          }

          // On-load cleanup: backend says this user has no active match request → remove stale ids from localStorage
          // so CarePriceUI won't restore an invalid id (e.g. from a previous session or different user).
          if (!mrId) {
            try {
              window.localStorage.removeItem(LAST_MATCH_REQUEST_ID_KEY);
              const keys = Object.keys(window.localStorage || {});
              keys.forEach((k) => {
                if (k.startsWith("healbid:careprice:submitted:")) {
                  window.localStorage.removeItem(k);
                }
              });
            } catch (_) {}
          }

          // If treatment completed, clear patient-end restore keys
          if (treatmentCompleted) {
            try {
              window.localStorage.removeItem(LAST_MATCH_REQUEST_ID_KEY);
              const keys = Object.keys(window.localStorage || {});
              for (const k of keys) {
                if (k.startsWith("healbid:careprice:submitted:")) {
                  window.localStorage.removeItem(k);
                }
              }
            } catch (_) {}
          }
        }
      } catch (_) {
        // ignore; fallback to localStorage-based restore
      }

      if (!cancelled) setBackendLatestLoading(false);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [routeMatchRequestId]);

  useEffect(() => {
    if (backendLatestLoading) return;
    const mrId = routeMatchRequestId || backendLatestMatchRequestId;
    if (!mrId) {
      setCarePriceWorkflowBlock(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const detailRes = await API.get(ENDPOINTS.GET_MATCH_REQUEST_DETAIL(mrId));
        const row =
          detailRes?.data?.request ??
          detailRes?.request ??
          detailRes?.data?.matchRequest ??
          null;
        const w = String(row?.requestWorkflowStatus || "").toUpperCase();
        if (!cancelled) {
          // PENDING: allow Care Price to save/edit draft budget (doctors still not notified).
          if (w === "REJECTED") {
            setCarePriceWorkflowBlock({ workflow: w, mrId: String(mrId) });
          } else {
            setCarePriceWorkflowBlock(null);
          }
        }
      } catch {
        if (!cancelled) setCarePriceWorkflowBlock(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [backendLatestLoading, backendLatestMatchRequestId, routeMatchRequestId]);

  const hasActiveBidFlow =
    Boolean(backendLatestMatchRequestId) && backendLatestTreatmentCompleted === false;
  // If admin completed treatment, reset patient-end flow and send user
  // back to category selection.
  useEffect(() => {
    if (backendLatestLoading) return;
    if (!backendLatestTreatmentCompleted) return;

    try {
      dispatch(clearMatchResults());
    } catch (_) {}

    // Only redirect if we're currently inside the bid flow route.
    if (location?.pathname && location.pathname.includes("care-price")) {
      navigate("/request/new", { replace: true });
    }
  }, [backendLatestLoading, backendLatestTreatmentCompleted, dispatch, location?.pathname, navigate]);

  const selectionList = useMemo(() => getSelectionList(selections), [selections]);
  const routeSubCategoryId = pickId(
    location?.state?.selectedSubCategory?._id,
    location?.state?.selectedSubCategory?.id,
    location?.state?.selectedSubCategory?.subCategoryId
  );

  const selectedSubCategoryId = useMemo(() => {
    if (routeSubCategoryId) return routeSubCategoryId;
    if (!selectionList.length) return "";
    const last = selectionList[selectionList.length - 1] || selectionList[0];
    return getSelectionSubCategoryId(last);
  }, [routeSubCategoryId, selectionList]);

  const [selectionsRequested, setSelectionsRequested] = useState(false);
  const [priceRequestedFor, setPriceRequestedFor] = useState("");

  const renderGateState = (title, subtitle, cta) => (
    <div style={{ padding: 24, background: "#dffdfb", minHeight: "100vh" }}>
      <div
        style={{
          maxWidth: 960,
          margin: "0 auto",
          background: "#fff",
          borderRadius: 16,
          border: "1px solid #d8efee",
          boxShadow: "0 12px 30px rgba(16,24,40,0.06)",
          padding: 20,
        }}
      >
        <div style={{ fontSize: 20, fontWeight: 700, color: "#0D0D0D" }}>{title}</div>
        <div style={{ marginTop: 10, color: "#5E5E5E" }}>{subtitle}</div>
        {cta ? (
          <div style={{ marginTop: 16 }}>
            <button
              type="button"
              onClick={cta.onClick}
              style={{
                border: 0,
                borderRadius: 10,
                padding: "10px 14px",
                fontWeight: 700,
                background: "#0aa9a9",
                color: "#fff",
                cursor: "pointer",
              }}
            >
              {cta.label}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );

  useEffect(() => {
    if (backendLatestLoading) return;
    if (selectionList.length) return;
    if (selectionsRequested) return;
    setSelectionsRequested(true);
    dispatch(getSelections());
  }, [dispatch, selectionList.length, selectionsRequested, backendLatestLoading]);

  useEffect(() => {
    if (!selectedSubCategoryId) return;
    if (priceRequestedFor && String(priceRequestedFor) === String(selectedSubCategoryId)) {
      return;
    }
    setPriceRequestedFor(selectedSubCategoryId);
    dispatch(getLowestPrice({ subCategoryId: selectedSubCategoryId }));
  }, [dispatch, selectedSubCategoryId, priceRequestedFor]);

  const prefetchedFromRoute = location?.state?.prefetchedPriceInfo;
  const prefetchedFromCache = useMemo(
    () => getRestoredPrefetchedPrice(selectedSubCategoryId),
    [selectedSubCategoryId]
  );
  const prefetchedToUse = prefetchedFromRoute || prefetchedFromCache;

  const priceReady = useMemo(() => {
    if (!selectedSubCategoryId) return false;
    // When we have prefetched price (from navigation or restored after reload), use it so we stay on the same flow and show doctor/hospital list instead of "Loading price range...".
    if (isPrefetchedPriceReady(prefetchedToUse, selectedSubCategoryId)) {
      return true;
    }
    if (!priceInfo) return false;

    if (priceInfo?.requestedSubCategoryId != null) {
      return String(priceInfo.requestedSubCategoryId) === String(selectedSubCategoryId);
    }
    if (priceInfo?.subCategoryId != null) {
      return String(priceInfo.subCategoryId) === String(selectedSubCategoryId);
    }

    const src = priceInfo?.data || priceInfo?.item || priceInfo || {};
    const lowest = Number(src?.lowestPrice ?? src?.minPrice ?? src?.minimumPrice);
    const highest = Number(src?.highestPrice ?? src?.maxPrice ?? src?.maximumPrice);
    return Number.isFinite(lowest) && Number.isFinite(highest);
  }, [priceInfo, selectedSubCategoryId, prefetchedToUse]);

  // Persist prefetched price so we can restore after reload (e.g. on first location grant).
  useEffect(() => {
    if (!selectedSubCategoryId) return;
    const fromRoute = location?.state?.prefetchedPriceInfo;
    if (fromRoute && isPrefetchedPriceReady(fromRoute, selectedSubCategoryId)) {
      setRestoredPrefetchedPrice(selectedSubCategoryId, fromRoute);
    }
  }, [selectedSubCategoryId, location?.state?.prefetchedPriceInfo]);

  useEffect(() => {
    if (!priceReady) return;
    if (location?.state?.prefetchedPriceInfo) return;
    // When priceReady from Redux, keep it in route state so CarePriceUI can initialize instantly.
    const statePriceInfo = priceInfo || prefetchedToUse;
    if (!statePriceInfo) return;
    navigate(location.pathname, {
      replace: true,
      state: {
        ...(location.state || {}),
        prefetchedPriceInfo: statePriceInfo,
      },
    });
  }, [priceReady, location.pathname, location.state, navigate, priceInfo, prefetchedToUse]);

  // When we have restored prefetch from cache but not in route state, put it in state so CarePriceUI gets it.
  useEffect(() => {
    if (!prefetchedFromCache || location?.state?.prefetchedPriceInfo) return;
    if (!selectedSubCategoryId || !isPrefetchedPriceReady(prefetchedFromCache, selectedSubCategoryId)) return;
    navigate(location.pathname, {
      replace: true,
      state: {
        ...(location.state || {}),
        prefetchedPriceInfo: prefetchedFromCache,
      },
    });
  }, [prefetchedFromCache, selectedSubCategoryId, location?.pathname, location?.state, navigate]);

  if (!backendLatestLoading && carePriceWorkflowBlock) {
    const { workflow: bw, mrId } = carePriceWorkflowBlock;
    const isReqRejected = bw === "REJECTED";
    return renderGateState(
      isReqRejected ? "Request not continuing" : "Documents under review",
      isReqRejected
        ? "This care request cannot continue yet. Open it for details and next steps."
        : "We’re checking your documents. You’ll be notified when you can share your surgery budget.",
      {
        label: "View care request",
        onClick: () => navigate(`/patient-match-request/${encodeURIComponent(mrId)}`),
      }
    );
  }

  // Resume an in-flight MR only when the patient did not open Care Price with an explicit
  // procedure in route state (e.g. new pick from onboarding). Otherwise we'd bind the wizard
  // to the latest MR, refetch bids, and it feels like "opening the page placed a bid".
  if (hasActiveBidFlow) {
    if (routeMatchRequestId) {
      return <CarePriceUI initialMatchRequestId={routeMatchRequestId} />;
    }
    // Do not auto-mount Care Price against the latest match request when the patient opens
    // `/care-price` from the nav with no intent — they should pick procedure + search + submit here,
    // or resume from dashboard / request detail with an explicit `matchRequestId` in route state.
  }

  // Avoid flashing "couldn't find procedure" while redirecting after treatment completed.
  if (!backendLatestLoading && backendLatestTreatmentCompleted) {
    return renderGateState("Redirecting...", "Taking you to procedure selection.");
  }

  if (backendLatestLoading) {
    return renderGateState(
      "Loading your treatment...",
      "Preparing your budget screen and matching doctors."
    );
  }

  if (!selectedSubCategoryId) {
    // If we are restoring from backend, show loading.
    if (backendLatestLoading) {
      return renderGateState(
        "Restoring your budget...",
        "Loading your previous request and doctor replies."
      );
    }

    // If we are still fetching selections, show loading (not "select first").
    if (!selectionList.length && selectionsRequested && !error) {
      return renderGateState(
        "Preparing your budget...",
        "Loading your saved category and procedure."
      );
    }

    return renderGateState(
      "Selection required",
      "We could not find your selected procedure. Please select a category and subcategory first.",
      { label: "Go to category selection", onClick: () => navigate("/request/new") }
    );
  }

  if (!priceReady) {
    if (error) {
      return renderGateState(
        "Could not load price range",
        typeof error === "string" ? error : "Something went wrong while fetching prices for this procedure.",
        {
          label: "Try again",
          onClick: () => {
            dispatch(clearPatientFlowError());
            dispatch(getLowestPrice({ subCategoryId: selectedSubCategoryId }));
          },
        }
      );
    }
    return renderGateState(
      "Loading price range...",
      "Fetching lowest, highest and suggested bid for the selected procedure."
    );
  }

  return <CarePriceUI />;
}

